"use client";
import * as React from "react";
import { Combobox as BaseUICombobox, ComboboxTriggerProps } from "@base-ui/react/combobox";
import { Check, ChevronDown, X } from "lucide-react";
import Label from "@/components/frontend/ui/form/inputs/label";
import Description from "@/components/frontend/ui/form/inputs/description";
import FieldError from "@/components/frontend/ui/form/inputs/field-error";
import { cn } from "@/lib/utils";
import "./style.scss";
import { FormVariant } from "@/lib/types/form/form";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";

export type ComboboxOption = {
	label: string;
	value: string;
};

type ComboboxValueProps =
	| {
			multiple: true;
			onValueChange?: (value: ComboboxOption[], e: BaseUICombobox.Root.ChangeEventDetails) => void;
			value?: ComboboxOption[];
			defaultValue?: ComboboxOption[];
	  }
	| {
			multiple?: false;
			onValueChange?: (value: ComboboxOption | null, e: BaseUICombobox.Root.ChangeEventDetails) => void;
			search?: boolean;
			value?: ComboboxOption;
			defaultValue?: ComboboxOption;
	  };

export interface ComboboxBaseProps extends Omit<ComboboxTriggerProps, "value"> {
	label?: string;
	hideLabel?: boolean;
	description?: string | null;
	error?: string;
	placeholder?: string;
	noResultsMessage?: string;
	containerClassName?: string;
	selectedClassName?: string;
	itemClassName?: string;
	required?: boolean;
	clearable?: boolean;
	creatable?: boolean;
	formVariant?: FormVariant;
	options?: ComboboxOption[];
	onInputValueChange?: (v: string) => void;
}

export type ComboboxProps = ComboboxBaseProps & ComboboxValueProps;

const Combobox: React.FC<ComboboxProps> = ({
	label,
	hideLabel,
	description,
	error,
	options,
	onValueChange,
	noResultsMessage = "No results",
	formVariant,
	clearable = true,
	creatable = false,
	onInputValueChange,
	...props
}) => {
	const generatedId = React.useId();
	const id = props?.id ?? generatedId;
	const chipsRef = React.useRef<HTMLDivElement>(null);
	const [inputValue, setInputValue] = React.useState("");
	const isItemEqualToValue = React.useCallback((itemValue: ComboboxOption, value: ComboboxOption) => itemValue?.value === value?.value, []);

	const items = React.useMemo(() => {
		const base = options ?? [];
		if (!creatable) return base;

		const trimmed = inputValue.trim();
		if (!trimmed) return base;

		const normalized = trimmed.toLowerCase();
		const exists = base.some((option) => option.value.toLowerCase() === normalized || option.label.toLowerCase() === normalized);
		if (exists) return base;

		return [...base, { value: trimmed, label: trimmed }];
	}, [creatable, inputValue, options]);

	const handleInputValueChange = React.useCallback(
		(value: string) => {
			setInputValue(value);
			onInputValueChange?.(value);
		},
		[onInputValueChange],
	);

	return (
		<BaseUICombobox.Root
			items={items}
			id={id}
			filter={!props.multiple && !props.search ? null : undefined}
			onValueChange={onValueChange as never}
			multiple={props.multiple}
			autoHighlight={creatable}
			onInputValueChange={handleInputValueChange}
			defaultValue={props.defaultValue as never}
			value={props.value as never}
			isItemEqualToValue={isItemEqualToValue}
		>
			<div className={cn("flex flex-col gap-1", props.containerClassName)}>
				{label && (
					<Label variant="native" htmlFor={id} required={props.required} visible={!hideLabel}>
						{label}
					</Label>
				)}
				{props.multiple ? (
					<BaseUICombobox.Chips
						ref={chipsRef}
						className="ms-select-trigger relative flex flex-wrap items-center gap-1 w-full text-base focus-within:outline-2 focus-within:-outline-offset-1"
					>
						<BaseUICombobox.Value>
							{(values: ComboboxOption[] | undefined) => (
								<>
									{(values ?? []).map((item) => (
										<BaseUICombobox.Chip key={item.value} className="ms-select-chip flex items-center gap-1 rounded px-2 py-0.5 text-sm">
											<span className={props.selectedClassName}>{item.label}</span>
											<BaseUICombobox.ChipRemove className="flex items-center justify-center rounded-full p-0.5 hover:opacity-70">
												<X className="size-3" />
											</BaseUICombobox.ChipRemove>
										</BaseUICombobox.Chip>
									))}

									<BaseUICombobox.Input
										placeholder={(values ?? []).length === 0 ? props.placeholder : undefined}
										id={id}
										className="sm:min-w-20 w-full sm:w-auto flex-1 border-none bg-transparent p-0 text-base outline-none"
									/>

									<div className="absolute right-2 top-(--ms-form-el-padding-top) flex h-6 items-center justify-center">
										{clearable && (
											<BaseUICombobox.Clear
												className="ms-select-control flex w-6 items-center justify-center rounded bg-transparent p-0"
												aria-label="Clear selection"
											>
												<X className="size-4" />
											</BaseUICombobox.Clear>
										)}
										<BaseUICombobox.Trigger
											className="ms-select-control flex w-6 items-center justify-center rounded bg-transparent p-0"
											aria-label="Open popup"
										>
											<ChevronDown className="size-4" />
										</BaseUICombobox.Trigger>
									</div>
								</>
							)}
						</BaseUICombobox.Value>
					</BaseUICombobox.Chips>
				) : (
					<div className="relative">
						{props.search ? (
							<BaseUICombobox.Input
								placeholder={props.placeholder}
								id={id}
								className="ms-select-trigger w-full text-base focus:outline-2 focus:-outline-offset-1"
							/>
						) : (
							<>
								<BaseUICombobox.Trigger
									id={id}
									className="ms-select-trigger flex justify-between w-full pr-14 text-base focus:outline-2 focus:-outline-offset-1"
									aria-label="Open popup"
								>
									<span className="grow text-left">
										<BaseUICombobox.Value
											placeholder={<span className={cn("opacity-60 truncate text", props.selectedClassName)}>{props.placeholder}</span>}
										/>
									</span>
								</BaseUICombobox.Trigger>
								<div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center">
									{clearable && (
										<BaseUICombobox.Clear
											className="ms-select-control flex w-6 items-center justify-center rounded bg-transparent p-0"
											aria-label="Clear selection"
										>
											<X className="size-4" />
										</BaseUICombobox.Clear>
									)}
									<BaseUICombobox.Icon className="ms-select-control pointer-events-none flex w-6 items-center justify-center rounded bg-transparent p-0">
										<ChevronDown className="size-4" />
									</BaseUICombobox.Icon>
								</div>
							</>
						)}

						{props.search && (
							<div className={cn("absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center")}>
								{clearable && (
									<BaseUICombobox.Clear
										className="ms-select-control flex w-6 items-center justify-center rounded bg-transparent p-0"
										aria-label="Clear selection"
									>
										<X className="size-4" />
									</BaseUICombobox.Clear>
								)}
								<BaseUICombobox.Trigger
									className={cn("ms-select-control flex w-6 items-center justify-center rounded bg-transparent p-0")}
									aria-label="Open popup"
								>
									<ChevronDown className="size-4" />
								</BaseUICombobox.Trigger>
							</div>
						)}
					</div>
				)}
				{description && <Description>{description}</Description>}
				<FieldError message={error} />
			</div>

			<BaseUICombobox.Portal>
				<BaseUICombobox.Positioner
					anchor={props.multiple ? chipsRef : undefined}
					className={cn("z-1010 outline-none", "ms-form-" + resolveRelationshipId(formVariant))}
					sideOffset={4}
				>
					<BaseUICombobox.Popup className="ms-select-popup w-(--anchor-width) max-h-92 max-w-(--available-width) origin-(--transform-origin) outline-1 transition-[transform,scale,opacity] data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 duration-100">
						<BaseUICombobox.Empty className="p-4 text-[0.925rem] leading-4 empty:m-0 empty:p-0">{noResultsMessage}</BaseUICombobox.Empty>
						<BaseUICombobox.List className="outline-0 p-2 overflow-y-auto scroll-py-2 py-2 overscroll-contain max-h-[min(23rem,var(--available-height))] data-empty:p-0">
							{(item: ComboboxOption, index: number) => (
								<BaseUICombobox.Item
									key={`${item.value}-${index}`}
									value={item}
									className="ms-select-option grid cursor-default grid-cols-[0.75rem_1fr] items-center gap-2 py-2 pr-8 pl-4 text-base leading-4 outline-none select-none data-highlighted:relative data-highlighted:z-0 data-highlighted:before:absolute data-highlighted:before:inset-x-2 data-highlighted:before:inset-y-0 data-highlighted:before:z-[-1] data-highlighted:before:rounded-sm"
								>
									<BaseUICombobox.ItemIndicator className="col-start-1">
										<Check className="size-3" />
									</BaseUICombobox.ItemIndicator>
									<div className={cn("col-start-2", props.itemClassName)}>{item.label}</div>
								</BaseUICombobox.Item>
							)}
						</BaseUICombobox.List>
					</BaseUICombobox.Popup>
				</BaseUICombobox.Positioner>
			</BaseUICombobox.Portal>
		</BaseUICombobox.Root>
	);
};

export default Combobox;
